import React from "react";
import { motion } from "motion/react";
import { Check, X, CalendarCheck, ArrowLeft } from "lucide-react";
import { ProfileIcon } from "@/components/icons/nav";
import { MailIcon } from "@/components/icons/actions";
import { demoLongRange, demoWeek } from "../demoCalendar";

// Demande de congé du mardi au jeudi, dans deux semaines.
const leaveWeek = demoWeek(2);
const leaveDates = demoLongRange(leaveWeek.days[1], leaveWeek.days[3]);

interface Props {
  reducedMotion?: boolean;
}

const tabs = ["À traiter", "Calendrier", "Historique", "Soldes"];

export const LeavesScene: React.FC<Props> = ({ reducedMotion }) => {
  const [approved, setApproved] = React.useState(reducedMotion);
  const [emailSent, setEmailSent] = React.useState(reducedMotion);

  React.useEffect(() => {
    if (reducedMotion) return;
    const t1 = setTimeout(() => setApproved(true), 2400);
    const t2 = setTimeout(() => setEmailSent(true), 3600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reducedMotion]);

  return (
    <div className="px-3 md:px-6 pb-6 pt-3 space-y-3">
      {/* Back link mock */}
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <ArrowLeft className="w-3 h-3" />
        Tableau de bord
      </div>

      {/* PageHero */}
      <div className="glass-subtle shadow-glass rounded-xl border border-border/50 px-3 md:px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Console RH</div>
        <h1 className="text-[15px] font-bold text-foreground">Congés du magasin</h1>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Traitez les demandes, suivez la charge sur le mois et retrouvez l'historique.
        </p>
      </div>

      {/* Tabs — scrollable on mobile */}
      <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
        <div className="glass-subtle shadow-glass rounded-[1rem] p-1 inline-flex gap-1 min-w-max">
          {tabs.map((t, i) => (
            <span
              key={t}
              className={`h-7 px-3 inline-flex items-center text-[11px] font-medium rounded-md whitespace-nowrap ${
                i === 0 ? "bg-background text-foreground shadow-sm ring-1 ring-border/40" : "text-muted-foreground"
              }`}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* SectionCard glass */}
      <div className="bg-card border rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-muted/10">
          <div className="text-[12px] font-semibold text-foreground">Demandes à traiter</div>
          <div className="text-[10px] text-muted-foreground">Validez, refusez ou inspectez les demandes en attente.</div>
        </div>

        <div className="p-3.5">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-card border rounded-xl p-3.5 transition-colors ${
              approved ? "border-success/40" : "border-warning/40"
            }`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <ProfileIcon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-foreground">Sarah Mercier</div>
                <div className="text-[10px] text-muted-foreground">Adjointe · Solde CP : 15 j</div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                approved ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
              }`}>
                {approved ? "APPROUVÉ" : "EN ATTENTE"}
              </span>
            </div>

            <div className="bg-muted/15 rounded-lg p-2.5 mb-3">
              <div className="flex items-center gap-2 text-[12px] text-foreground mb-0.5">
                <CalendarCheck className="w-3.5 h-3.5 text-primary" />
                <span className="font-semibold">Congé payé · 3 jours ouvrés</span>
              </div>
              <div className="text-[10px] text-muted-foreground">{leaveDates}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Motif : « Visite famille »</div>
              <div className="text-[10px] text-muted-foreground mt-1 pt-1 border-t border-border/40">
                Aucun chevauchement détecté avec l'équipe sur ces dates.
              </div>
            </div>

            <div className="flex gap-2">
              <motion.button
                animate={!approved && !reducedMotion ? { scale: [1, 1.04, 1] } : {}}
                transition={{ duration: 1.2, repeat: approved ? 0 : Infinity }}
                className={`flex-1 h-8 rounded-md text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                  approved ? "bg-success/15 text-success" : "bg-success text-white"
                }`}
              >
                <Check className="w-3 h-3" />
                {approved ? "Approuvé" : "Approuver"}
              </motion.button>
              <button className="px-3 h-8 rounded-md text-[11px] font-semibold bg-muted text-muted-foreground flex items-center justify-center gap-1.5">
                <X className="w-3 h-3" />
                Refuser
              </button>
            </div>

            {emailSent && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 flex items-center gap-2 text-[10px] text-muted-foreground"
              >
                <MailIcon className="w-3 h-3 text-success" />
                Email envoyé à Sarah · planning mis à jour automatiquement
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};
